import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Vazio, Carregando, Folha } from '../componentes/ui'
import './lista-espera.css'

/**
 * Lista de espera.
 *
 * ─── DUAS TELAS, UMA FILA ──────────────────────────────────────────
 *
 * `PainelEspera` é a fila inteira, num painel lateral da agenda: quem
 * está esperando, há quanto tempo, com que preferência.
 *
 * `QuemCabeAqui` é a que vale dinheiro. Abre quando um horário vaga e
 * mostra só quem CABE naquele buraco — já filtrado por dia da semana,
 * faixa de horário, tentativas e agendamento futuro. O telefone vem na
 * lista de propósito: a pessoa vai ligar agora, e obrigá-la a abrir a
 * ficha do paciente no meio da ligação é como o recurso deixa de ser
 * usado.
 *
 * ─── POR QUE NÃO É UMA TELA PRÓPRIA NO MENU ────────────────────────
 *
 * Lista de espera fora da agenda é lista que ninguém abre. Ela precisa
 * estar onde o buraco aparece, no segundo em que aparece.
 */

type Pedido = {
  id: string; paciente_id: string; paciente: string; telefone: string | null
  procedimento_id: string | null; procedimento: string | null
  profissional: string | null
  dias_semana: number[]; hora_de: string; hora_ate: string
  prioridade: number; observacao: string | null
  estado: string; tentativas: number
  contatado_em: string | null; expira_em: string; dias_esperando: number
}

type Candidato = {
  id: string; paciente_id: string; paciente: string; telefone: string
  procedimento: string | null; prioridade: number
  dias_esperando: number; tentativas: number; observacao: string | null
}

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

const PRIORIDADE = ['normal', 'preferencial', 'urgente']

const espera = (d: number) =>
  d === 0 ? 'entrou hoje' : d === 1 ? 'espera há 1 dia' : `espera há ${d} dias`

/** "seg, qua e sex" — vazio significa qualquer dia. */
function quandoAceita(p: Pedido): string {
  const dias = p.dias_semana.length === 0
    ? 'qualquer dia'
    : p.dias_semana.map((d) => DIAS[d]).join(', ')
  const faixa = p.hora_de === '00:00' && p.hora_ate === '23:59'
    ? 'qualquer horário'
    : `${p.hora_de}–${p.hora_ate}`
  return `${dias} · ${faixa}`
}

/* ══ o painel da fila ══════════════════════════════════════════════ */

export function PainelEspera({ podeEditar, aoFechar }: {
  podeEditar: boolean; aoFechar: () => void
}) {
  const [itens, setItens] = useState<Pedido[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [novo, setNovo] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const r = await api<{ itens: Pedido[] }>('/v1/espera')
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar a fila.')
    } finally { setCarregando(false) }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  async function resolver(id: string, estado: string) {
    try {
      await api(`/v1/espera/${id}/resolver`, { metodo: 'POST', corpo: { estado } })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível encerrar.')
    }
  }

  return (
    <Folha titulo="Lista de espera"
           descricao={`${itens.length} pedido(s) na fila. Quando um horário vaga, a agenda mostra quem cabe nele.`}
           aoFechar={aoFechar}
           acoes={
             <>
               {podeEditar && (
                 <button type="button" className="botao botao--fantasma"
                         onClick={() => setNovo(true)}>
                   Adicionar
                 </button>
               )}
               <button type="button" className="botao" onClick={aoFechar}>Fechar</button>
             </>
           }>
      {carregando ? <Carregando /> : (
        <>
          {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

          {itens.length === 0 ? (
            <Vazio
              mensagem="Ninguém na fila."
              sugestao="Quando alguém pedir “me avisa se abrir”, ponha aqui. É o que transforma um cancelamento em atendimento em vez de horário vazio."
              acao={podeEditar && (
                <button className="botao" onClick={() => setNovo(true)}>
                  Adicionar alguém
                </button>
              )}
            />
          ) : (
            <ul className="le-fila">
              {itens.map((p) => (
                <li key={p.id} className={`le-item p${p.prioridade}`}>
                  <div className="le-item__corpo">
                    <span className="le-item__nome">
                      {p.paciente}
                      {p.prioridade > 0 && (
                        <span className={`le-selo p${p.prioridade}`}>
                          {PRIORIDADE[p.prioridade]}
                        </span>
                      )}
                      {p.tentativas > 0 && (
                        <span className="le-selo le-selo--tent">
                          {p.tentativas} tentativa{p.tentativas > 1 ? 's' : ''}
                        </span>
                      )}
                    </span>
                    <span className="ajuda">
                      {p.procedimento ?? 'qualquer procedimento'}
                      {p.profissional && ` · ${p.profissional}`}
                      {' · '}{quandoAceita(p)}
                    </span>
                    <span className="ajuda">
                      {espera(p.dias_esperando)}
                      {p.observacao && ` · ${p.observacao}`}
                    </span>
                  </div>
                  {podeEditar && (
                    <div className="le-item__acoes">
                      {/* `tel:` e nunca `wa.me`: o segundo manda o telefone
                          do paciente para um domínio que não é nosso. */}
                      {p.telefone && (
                        <a className="le-acao" href={`tel:${p.telefone}`}>Ligar</a>
                      )}
                      <button className="le-acao" onClick={() => resolver(p.id, 'DESISTIU')}>
                        Tirar da fila
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {novo && (
        <NovoPedido aoFechar={() => setNovo(false)}
                    aoSalvar={async () => { setNovo(false); await carregar() }} />
      )}
    </Folha>
  )
}

/* ══ quem cabe neste horário ═══════════════════════════════════════ */

/**
 * A tela que justifica a feature.
 *
 * Não lista a fila inteira: lista quem cabe NESTE horário. A diferença
 * entre as duas coisas é a diferença entre um recurso usado e um campo
 * que ninguém abre.
 */
export function QuemCabeAqui({ inicio, procedimentoId, profissionalId, aoFechar }: {
  inicio: string
  procedimentoId?: string | null
  profissionalId?: string | null
  aoFechar: () => void
}) {
  const [itens, setItens] = useState<Candidato[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [chamados, setChamados] = useState<Set<string>>(new Set())

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const q = new URLSearchParams({ inicio })
      if (procedimentoId) q.set('procedimento', procedimentoId)
      if (profissionalId) q.set('profissional', profissionalId)
      const r = await api<{ itens: Candidato[] }>(`/v1/espera/encaixe?${q}`)
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível buscar.')
    } finally { setCarregando(false) }
  }, [inicio, procedimentoId, profissionalId])

  useEffect(() => { void carregar() }, [carregar])

  async function contatar(c: Candidato) {
    try {
      await api(`/v1/espera/${c.id}/contatar`, { metodo: 'POST' })
      setChamados((s) => new Set(s).add(c.id))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível registrar.')
    }
  }

  async function resolver(c: Candidato, estado: string) {
    try {
      await api(`/v1/espera/${c.id}/resolver`, { metodo: 'POST', corpo: { estado } })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível encerrar.')
    }
  }

  const quando = new Date(inicio).toLocaleString('pt-BR',
    { weekday: 'long', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' })

  return (
    <Folha titulo="Quem cabe neste horário"
           descricao={`${quando} · já filtrado pela preferência de cada um`}
           aoFechar={aoFechar}
           acoes={<button type="button" className="botao" onClick={aoFechar}>Fechar</button>}>
      {carregando ? <Carregando /> : (
        <>
          {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

          {itens.length === 0 ? (
            <Vazio
              mensagem="Ninguém da fila cabe neste horário."
              sugestao="Pode ser que a fila esteja vazia, ou que quem espera prefira outro dia ou outra faixa. Quem já foi chamado hoje também não aparece de novo."
            />
          ) : (
            <ol className="le-cand">
              {itens.map((c, i) => (
                <li key={c.id} className={chamados.has(c.id) ? 'is-chamado' : ''}>
                  <span className="le-cand__pos">{i + 1}</span>
                  <div className="le-cand__corpo">
                    <span className="le-cand__nome">
                      {c.paciente}
                      {c.prioridade > 0 && (
                        <span className={`le-selo p${c.prioridade}`}>
                          {PRIORIDADE[c.prioridade]}
                        </span>
                      )}
                    </span>
                    <span className="ajuda">
                      {c.procedimento ?? 'qualquer procedimento'} · {espera(c.dias_esperando)}
                      {c.tentativas > 0 && ` · ${c.tentativas} tentativa(s) antes`}
                    </span>
                    {c.observacao && <span className="ajuda">{c.observacao}</span>}
                  </div>
                  <div className="le-cand__acoes">
                    <a className="le-acao le-acao--forte" href={`tel:${c.telefone}`}
                       onClick={() => void contatar(c)}>
                      {c.telefone}
                    </a>
                    {chamados.has(c.id) && (
                      <>
                        <button className="le-acao" onClick={() => resolver(c, 'AGENDADO')}>
                          Marcou
                        </button>
                        <button className="le-acao" onClick={() => resolver(c, 'DESISTIU')}>
                          Não quis
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <p className="ajuda le-nota">
            Tocar no telefone registra a tentativa. Depois de três sem sucesso, o
            pedido para de ser oferecido — insistir com quem nunca atende é o que
            faz a recepção deixar de confiar na lista.
          </p>
        </>
      )}
    </Folha>
  )
}

/* ══ entrar na fila ════════════════════════════════════════════════ */

type Opcao = { id: string; nome: string }

function NovoPedido({ aoFechar, aoSalvar }: {
  aoFechar: () => void; aoSalvar: () => Promise<void>
}) {
  const [busca, setBusca] = useState('')
  const [achados, setAchados] = useState<Opcao[]>([])
  const [paciente, setPaciente] = useState<Opcao | null>(null)
  const [procedimentos, setProcedimentos] = useState<Opcao[]>([])
  const [proc, setProc] = useState('')
  const [dias, setDias] = useState<number[]>([])
  const [de, setDe] = useState('00:00')
  const [ate, setAte] = useState('23:59')
  const [prioridade, setPrioridade] = useState(0)
  const [obs, setObs] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<{ itens: Opcao[] }>('/v1/procedimentos?limite=100')
        setProcedimentos(r.itens)
      } catch { /* a lista é opcional: dá para esperar por qualquer coisa */ }
    })()
  }, [])

  useEffect(() => {
    if (busca.trim().length < 3) { setAchados([]); return }
    const t = setTimeout(() => {
      void (async () => {
        try {
          const r = await api<{ itens: Opcao[] }>(
            `/v1/pacientes?busca=${encodeURIComponent(busca.trim())}&limite=8`)
          setAchados(r.itens)
        } catch { setAchados([]) }
      })()
    }, 250)
    return () => clearTimeout(t)
  }, [busca])

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    if (!paciente) { setErro('Escolha o paciente.'); return }
    if (ate <= de) { setErro('A faixa de horário está invertida.'); return }
    setSalvando(true); setErro(null)
    try {
      await api('/v1/espera', {
        metodo: 'POST',
        corpo: {
          paciente_id: paciente.id,
          ...(proc ? { procedimento_id: proc } : {}),
          ...(dias.length ? { dias_semana: dias } : {}),
          hora_de: de, hora_ate: ate, prioridade,
          ...(obs.trim() ? { observacao: obs.trim() } : {}),
        },
      })
      await aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi
        ? (err.status === 409
            ? 'Este paciente já está na fila para este procedimento.'
            : err.message)
        : 'Não foi possível adicionar.')
      setSalvando(false)
    }
  }

  return (
    <Folha titulo="Adicionar à lista de espera"
           descricao="Quanto mais específica a preferência, mais útil o encaixe."
           aoFechar={aoFechar}
           acoes={
             <button type="submit" form="form-espera" className="botao"
                     disabled={salvando || !paciente}>
               {salvando ? 'Salvando…' : 'Adicionar'}
             </button>
           }>
      <form id="form-espera" onSubmit={salvar} className="le-form">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="le-pac">Paciente</label>
          {paciente ? (
            <div className="le-escolhido">
              <strong>{paciente.nome}</strong>
              <button type="button" className="le-acao"
                      onClick={() => { setPaciente(null); setBusca('') }}>trocar</button>
            </div>
          ) : (
            <>
              <input id="le-pac" className="entrada" autoFocus
                     placeholder="Nome do paciente" value={busca}
                     onChange={(e) => setBusca(e.target.value)} />
              {achados.length > 0 && (
                <ul className="le-achados">
                  {achados.map((p) => (
                    <li key={p.id}>
                      <button type="button" onClick={() => setPaciente(p)}>{p.nome}</button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="campo">
          <label htmlFor="le-proc">Procedimento</label>
          <select id="le-proc" className="entrada" value={proc}
                  onChange={(e) => setProc(e.target.value)}>
            <option value="">Qualquer um</option>
            {procedimentos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </div>

        <fieldset className="le-bloco">
          <legend>Quando aceita</legend>
          <div className="le-dias">
            {DIAS.map((d, i) => (
              <label key={d} className={dias.includes(i) ? 'is-on' : ''}>
                <input type="checkbox" checked={dias.includes(i)}
                       onChange={(e) => setDias((s) =>
                         e.target.checked ? [...s, i] : s.filter((x) => x !== i))} />
                <span>{d}</span>
              </label>
            ))}
          </div>
          <span className="ajuda">
            {dias.length === 0
              ? 'Nenhum dia marcado significa qualquer dia — é o que mais encaixa.'
              : `Só ${dias.map((d) => DIAS[d]).join(', ')}.`}
          </span>

          <div className="le-faixa">
            <div className="campo">
              <label htmlFor="le-de">Das</label>
              <input id="le-de" className="entrada mono" type="time"
                     value={de} onChange={(e) => setDe(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="le-ate">Até</label>
              <input id="le-ate" className="entrada mono" type="time"
                     value={ate} onChange={(e) => setAte(e.target.value)} />
            </div>
          </div>
        </fieldset>

        <div className="campo">
          <label htmlFor="le-prio">Prioridade</label>
          <select id="le-prio" className="entrada" value={prioridade}
                  onChange={(e) => setPrioridade(Number(e.target.value))}>
            <option value={0}>Normal</option>
            <option value={1}>Preferencial</option>
            <option value={2}>Urgente</option>
          </select>
          {/* A ordem da fila é prioridade primeiro, espera depois. Dizer
              isso aqui evita que "urgente" vire o padrão de todo mundo. */}
          <span className="ajuda">
            Quem tem prioridade passa na frente de quem espera há mais tempo.
            Use com parcimônia, senão a fila volta a ser por ordem de chegada.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="le-obs">Observação</label>
          <input id="le-obs" className="entrada" maxLength={300}
                 placeholder="ex.: só consegue vir de carona na terça"
                 value={obs} onChange={(e) => setObs(e.target.value)} />
        </div>
      </form>
    </Folha>
  )
}
